import { EditSecretOperation } from 'apps/sigma2-ui/src/secrets/lib/types';
import { OnOrganizationSecretEditCompleteFn } from 'apps/sigma2-ui/src/secrets/lib/types';
import { EditSecretOperations } from 'apps/sigma2-ui/src/secrets/lib/types';
import { useSimpleReadWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadWritePattern';
import { UpdateOrganizationSecretRequestBodySchema } from 'libs/end-to-end/src/types/secrets/create-or-update-organization-secret-request-body';
import { OrganizationSecretItem } from 'libs/end-to-end/src/types/secrets/organization-secret-items';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { OrganizationSecretEditComponentHook } from 'apps/sigma2-ui/src/secrets/lib/types';

export const useOrganizationSecretEditComponent = ({
  organizationId,
  editSecretId,
  onComplete,
}: {
  organizationId: string;
  editSecretId: string;
  onComplete: OnOrganizationSecretEditCompleteFn;
}): OrganizationSecretEditComponentHook => {
  const writeGetWriteUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/secrets/${editSecretId}`;
  }, [editSecretId, organizationId]);

  const writeGetReadUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/secrets/${editSecretId}`;
  }, [editSecretId, organizationId]);

  const writePattern = useSimpleReadWritePattern({
    getWriteUrlFn: writeGetWriteUrlFn,
    writeMethod: 'PUT',
    writeSchema: UpdateOrganizationSecretRequestBodySchema,
    canWrite: true,
    getReadUrlFn: writeGetReadUrlFn,
    canRead: true,
    responseSchema: OrganizationSecretItem,
  });

  useEffect(() => {
    if (writePattern.status === 'done' && !writePattern.saved) {
      setEditSecretValue(null);
      setEditSecretDescription(writePattern.entity.secretDescription);
      setEditSecretValueOperation('no-change');
      return;
    }

    if (writePattern.status === 'done' && writePattern.saved) {
      onComplete(editSecretId);
    }
  }, [editSecretId, onComplete, writePattern, writePattern.status]);

  const [editSecretValueOperation, setEditSecretValueOperation] =
    useState<EditSecretOperation>('no-change');
  const [editSecretDescription, setEditSecretDescription] =
    useState<string>('');
  const [editSecretValue, setEditSecretValue] = useState<string | null>(null);

  useEffect(() => {
    if (editSecretValueOperation === 'clear') {
      setEditSecretValue('');
      return;
    }

    if (editSecretValueOperation === 'edit') {
      setEditSecretValue('');
      return;
    }

    if (editSecretValueOperation === 'no-change') {
      setEditSecretValue(null);
      return;
    }
  }, [editSecretValueOperation]);

  return useMemo(() => {
    return {
      setEditSecretValue,
      setEditSecretDescription,
      editSecretValue,
      editSecretDescription,
      editSecretButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          if (
            writePattern.status !== 'done' &&
            writePattern.status !== 'error'
          ) {
            return;
          }

          if (editSecretValueOperation === 'edit' && !editSecretValue) {
            return;
          }

          writePattern.saveEntity({
            secretDescription: editSecretDescription,
            secretValue: editSecretValue,
          });
        },
      },
      editSecretTitle: 'Edit Secret',
      editSecretButtonLabel: 'Save',
      editSecretDescriptionLabel: 'Description',
      editSecretValueLabel: 'Value',
      editSecretValueOperationLabel: 'Operation',
      editSecretValueOperation,
      editSecretValueOperations: EditSecretOperations.map((operation) => {
        return {
          optionValue: operation,
          optionLabel: operation,
        };
      }),
      setEditSecretValueOperation,
      editSecretButtonDisabled:
        editSecretValueOperation === 'edit' && !editSecretValue,
    };
  }, [
    editSecretDescription,
    editSecretValue,
    editSecretValueOperation,
    writePattern,
  ]);
};
